/**
 * Utilities for legislation HTML diff "view modes" (M-23) and multi-step comparison.
 *
 * Full diff markup uses <ins>/<del> embedded in the merged HTML. For "before" /
 * "after" views we reconstruct each version by removing the opposite side from
 * the DOM (not only hiding with CSS) so layout matches reading the real
 * document, with highlights on changed spans only.
 */

import type { TimelineItem } from "@/models/legislation/Timeline";

/**
 * Given a flat list of timeline items (all with valid accessionNumbers, sorted
 * chronologically old→new), return the inclusive slice between `fromAccessionNumber`
 * and `toAccessionNumber`.
 *
 * Items without an `accessionNumber` are silently skipped.
 *
 * Returns `null` when either endpoint cannot be found, or when `from` comes
 * after `to` in the array (i.e. the user selected a *newer* item as "compare
 * target", which doesn't make sense for a backward diff).
 */
export const getTimelineRange = (
  items: TimelineItem[],
  fromAccessionNumber: string,
  toAccessionNumber: string
): TimelineItem[] | null => {
  const withAccession = items.filter(
    (i): i is TimelineItem & { accessionNumber: string } =>
      i.accessionNumber !== null && i.accessionNumber !== undefined
  );

  const fromIdx = withAccession.findIndex(
    (i) => i.accessionNumber === fromAccessionNumber
  );
  const toIdx = withAccession.findIndex(
    (i) => i.accessionNumber === toAccessionNumber
  );

  if (fromIdx === -1 || toIdx === -1) return null;
  if (fromIdx >= toIdx) return null;

  return withAccession.slice(fromIdx, toIdx + 1);
};

/**
 * Order-agnostic variant of `getTimelineRange`: returns the inclusive slice
 * between two endpoints regardless of which one is earlier on the timeline.
 *
 * Used by compare so a target that sits *after* the current document still
 * yields a valid old→new range. Returns `null` when an endpoint is missing or
 * both endpoints resolve to the same item.
 */
export const getTimelineRangeBetween = (
  items: TimelineItem[],
  accessionA: string,
  accessionB: string
): TimelineItem[] | null => {
  const withAccession = items.filter(
    (i): i is TimelineItem & { accessionNumber: string } =>
      i.accessionNumber !== null && i.accessionNumber !== undefined
  );

  const idxA = withAccession.findIndex((i) => i.accessionNumber === accessionA);
  const idxB = withAccession.findIndex((i) => i.accessionNumber === accessionB);
  if (idxA === -1 || idxB === -1) return null;

  const [fromAccession, toAccession] =
    idxA <= idxB ? [accessionA, accessionB] : [accessionB, accessionA];

  return getTimelineRange(withAccession, fromAccession, toAccession);
};

/**
 * Flatten a potentially nested timeline tree into a flat array via naive
 * depth-first recursion, preserving the tree traversal order (preorder). Laws
 * are flat already; Bekendtgørelse trees keep their branch structure so the
 * timeline can be traversed via branches.
 */
export const flattenTimeline = (items: TimelineItem[]): TimelineItem[] =>
  items.flatMap((item) => [
    item,
    ...(item.children ? flattenTimeline(item.children) : []),
  ]);

export const DIFF_INS_SELECTOR = "ins.diffins, ins.diffmod";
export const DIFF_DEL_SELECTOR = "del.diffdel, del.diffmod";
export const DIFF_ALL_SELECTOR = `${DIFF_INS_SELECTOR}, ${DIFF_DEL_SELECTOR}`;

export const DIFF_HIGHLIGHT_AFTER_SELECTOR = "span.diff-highlight-after";
export const DIFF_HIGHLIGHT_BEFORE_SELECTOR = "span.diff-highlight-before";

/** Selector for block navigation for each view mode (diff = ins+del, before/after = highlight spans). */
export const getVisibleParagraphSelector = (
  filter: "diff" | "before" | "after"
): string => {
  switch (filter) {
    case "diff":
      return DIFF_ALL_SELECTOR;
    case "after":
      return DIFF_HIGHLIGHT_AFTER_SELECTOR;
    case "before":
      return DIFF_HIGHLIGHT_BEFORE_SELECTOR;
  }
};

const BLOCK_TAGS = new Set([
  "P", "LI", "TD", "TH", "BLOCKQUOTE", "PRE",
  "H1", "H2", "H3", "H4", "H5", "H6",
  "DT", "DD", "FIGCAPTION",
]);

/** Matches `BLOCK_TAGS` — used to align a diff block with the same block in full-version HTML. */
export const DIFF_BLOCK_SELECTOR =
  "p, li, td, th, blockquote, pre, h1, h2, h3, h4, h5, h6, dt, dd, figcaption";

/**
 * When inline diff markup omits `<del>` (insert-only) or `<ins>` (delete-only) inside a
 * block, foldout text cannot be reconstructed from the merged DOM alone. Map the navigated
 * block to the same block in the raw before/after snapshot by `id` or document order.
 */
export const findCorrespondingBlockInSnapshot = (
  paragraph: HTMLElement,
  snapshotHtml: string,
  diffRoot: HTMLElement
): HTMLElement | null => {
  if (!snapshotHtml?.trim() || !diffRoot) return null;

  const wrap = document.createElement("div");
  wrap.innerHTML = snapshotHtml;

  const id = paragraph.id;
  if (id) {
    try {
      const byId = wrap.querySelector(`#${CSS.escape(id)}`);
      if (byId instanceof HTMLElement) return byId;
    } catch {
      /* invalid id for selector */
    }
  }

  const blocksInDiff = Array.from(
    diffRoot.querySelectorAll<HTMLElement>(DIFF_BLOCK_SELECTOR)
  );
  const idx = blocksInDiff.indexOf(paragraph);
  if (idx < 0) return null;

  const blocksInSnap = Array.from(
    wrap.querySelectorAll<HTMLElement>(DIFF_BLOCK_SELECTOR)
  );
  const hit = blocksInSnap[idx];
  return hit instanceof HTMLElement ? hit : null;
};

const nearestBlock = (el: HTMLElement, root: HTMLElement): HTMLElement => {
  let node: HTMLElement | null = el.parentElement;
  let candidate: HTMLElement = el;
  while (node && node !== root) {
    if (BLOCK_TAGS.has(node.tagName)) {
      candidate = node;
      break;
    }
    node = node.parentElement;
  }
  return candidate;
};

/**
 * Given a CSS selector, return a de-duplicated list of the nearest block-level
 * ancestor for each matching element, preserving DOM order.
 */
export const collectParagraphs = (
  root: HTMLElement,
  selector: string
): HTMLElement[] => {
  const seen = new Set<HTMLElement>();
  const result: HTMLElement[] = [];
  for (const el of Array.from(root.querySelectorAll<HTMLElement>(selector))) {
    if (!el.textContent?.trim()) {
      continue;
    }
    const block = nearestBlock(el, root);
    if (!seen.has(block)) {
      seen.add(block);
      result.push(block);
    }
  }
  return result;
};

/**
 * Count insertion/deletion paragraph stats from raw diff HTML (in-memory parse).
 * Used so counts stay stable when the visible DOM uses reconstructed HTML.
 */
export const countDiffStatsFromString = (diffHtml: string): {
  insertions: number;
  deletions: number;
  allChangesCount: number;
} => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(
      `<div class="doc-root">${diffHtml}</div>`,
      "text/html"
    );
    const root = doc.body.firstElementChild as HTMLElement | null;
    if (!root) {
      return { insertions: 0, deletions: 0, allChangesCount: 0 };
    }
    const insParagraphs = collectParagraphs(root, DIFF_INS_SELECTOR);
    const delParagraphs = collectParagraphs(root, DIFF_DEL_SELECTOR);
    const allParagraphs = collectParagraphs(root, DIFF_ALL_SELECTOR);
    return {
      insertions: insParagraphs.length,
      deletions: delParagraphs.length,
      allChangesCount: allParagraphs.length,
    };
  } catch {
    return { insertions: 0, deletions: 0, allChangesCount: 0 };
  }
};

const replaceWithHighlightSpan = (
  container: HTMLElement,
  selector: string,
  className: string
): void => {
  container.querySelectorAll(selector).forEach((el) => {
    const span = document.createElement("span");
    span.className = className;
    while (el.firstChild) {
      span.appendChild(el.firstChild);
    }
    el.replaceWith(span);
  });
};

/**
 * Build HTML for "after" (current) or "before" (compare target) view:
 * opposite-side tags are removed from the tree; remaining diff tags become
 * neutral highlight spans so the document flows like the real version.
 * Only touches ins/del nodes emitted by htmldiff-js (diffins/diffmod/diffdel).
 */
export const buildReconstructedDiffViewHtml = (
  diffHtml: string,
  mode: "before" | "after"
): string => {
  if (typeof document === "undefined") {
    return diffHtml;
  }
  try {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = diffHtml;

    if (mode === "after") {
      wrapper.querySelectorAll(DIFF_DEL_SELECTOR).forEach((el) => el.remove());
      replaceWithHighlightSpan(
        wrapper,
        DIFF_INS_SELECTOR,
        "diff-highlight-after"
      );
    } else {
      wrapper.querySelectorAll(DIFF_INS_SELECTOR).forEach((el) => el.remove());
      replaceWithHighlightSpan(
        wrapper,
        DIFF_DEL_SELECTOR,
        "diff-highlight-before"
      );
    }

    return wrapper.innerHTML;
  } catch {
    return diffHtml;
  }
};
